import React, { FC, ReactNode, useEffect, useState } from 'react';
import { Button, Typography, Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useController } from 'react-hook-form';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import { M24MediaModel } from '@/declarations/models/M24MediaModel';
import {
  prepareMediaItemAsDAMSMediaModel,
  prepareMediaItemAsDMMediaModel,
  prepareMediaItemAsM24MediaModel,
} from '@/utils/MediaUtils';
import { ChildrenProp } from '@/declarations/ChildrenProp';
import { GenericMedia } from '@/declarations/GenericMedia';
import { DMMediaModel } from '@/declarations/models/DMMediaModel';
import { isM24MediaModel } from '@/utils/typeChecks';
import { useMediaEditorContext } from '@/editor/MediaEditor/MediaEditorContext';
import { Api } from '@/services/Api';
import { ExternalMediaModel, isDiMuMediaModel } from '@/declarations/models/ExternalMediaModel';
import { BlockSpecificPage } from '@/editor/lib/declarations/BlockSpecificPage';
import { BlockPath, useCurrentBlock } from '@/editor/PageEditor/CurrentBlockProvider';
import { BaseBlock } from '@/declarations/models/blocks/BaseBlock';
import { DamsMediaItem } from '@/declarations/models/DamsMediaResponse';
import { Settings } from '@/Settings';
import { MediaResourceType } from '@/declarations/models/MediaResourceType';
import ImageWithFocusPointSelector from './ImageWithFocusPointSelector';
import { useStore } from './store/Store';
import DeleteButton from './DeleteButton';
import Styles from '../assets/js/Styles';
import Container from './Container';
import FinderButton, { FinderType } from './Finder/FinderButton';

interface AddMediaObjectProps extends ChildrenProp {
  mediaObject?: M24MediaModel | ExternalMediaModel | DamsMediaItem | null;
  canEdit?: boolean;
  endChildren?: ReactNode;
  variant?: 'default' | 'banner';
  finderType?: FinderType.M24 | FinderType.DM | FinderType.DAMS;
}

function hasLocal(obj: any): obj is { local: any } {
  return obj && typeof obj === 'object' && 'local' in obj;
}
function hasUrl(obj: any): obj is { url: string } {
  return obj && typeof obj === 'object' && 'url' in obj;
}
function hasMimetype(obj: any): obj is { mimetype: string } {
  return obj && typeof obj === 'object' && 'mimetype' in obj;
}

/**
 *
 * @param mediaObject - get mediaObject from useWatch in parent since useController
 *                    does not update on external changes to the value
 * @param canEdit - If true you can edit the media from the module
 * @param children - overrides default render. Displayed when a media object is selected.
 */
export const AddMediaObject: FC<AddMediaObjectProps> = ({
  mediaObject,
  canEdit,
  children,
  endChildren,
  variant = 'default',
  finderType,
}) => {
  const { blockPath } = useCurrentBlock();
  const { t } = useTranslation('components');
  const { t: tCommon } = useTranslation('common');
  const { state } = useStore();
  const siteId = state.selectedSite?.id;
  const { setMediaId, setEditorOpen, editorOpen } = useMediaEditorContext();
  const isSuperAdmin = state.currentUser?.user?.is_super_admin;

  const {
    field: { onChange: onChangeMediaObject },
  } = useController<
    BlockSpecificPage<BaseBlock & { mediaobject?: M24MediaModel | ExternalMediaModel }>,
    `${BlockPath}.mediaobject`
  >({
    name: `${blockPath}.mediaobject`,
  });

  const isM24Media = isM24MediaModel(mediaObject);
  const [m24Item, setM24Item] = useState<M24MediaModel | undefined>(isM24Media ? mediaObject : undefined);

  useEffect(() => {
    if (isM24Media) {
      if (!editorOpen && canEdit && siteId && mediaObject.id) {
        const ctx = Api.getM24MediaResource(mediaObject.site_id, mediaObject.id);
        ctx
          .fetchDirect(null)
          .then((e) => e && setM24Item(e))
          .finally(ctx.abort);
      }
    } else setM24Item(undefined);
  }, [mediaObject, siteId, editorOpen, isM24Media, canEdit]);

  const handleEditMedia = () => {
    if (isM24Media && mediaObject.id) {
      setMediaId(mediaObject.id);
      setEditorOpen(true);
    }
  };

  const resetContentSettings = () => {
    onChangeMediaObject({
      ...mediaObject,
      alpha_on: false,
      vignette_on: false,
      gradient_on: false,
    });
  };

  const handleFocusPointChanged = (focusPoint: { x: string; y: string }) => {
    onChangeMediaObject({
      ...mediaObject,
      local: {
        ...(hasLocal(mediaObject) ? mediaObject.local : {}),
        x: focusPoint.x,
        y: focusPoint.y,
      },
    });
  };

  function getImageSrc(
    m24Media: M24MediaModel | undefined,
    mediaObj: M24MediaModel | ExternalMediaModel | DamsMediaItem | null | undefined,
  ): string {
    if (m24Media?.url) {
      return m24Media.url;
    }
    // Handle DamsMediaItem
    if (mediaObj && typeof mediaObj === 'object' && ('dimuId' in mediaObj || 'dmsId' in mediaObj)) {
      if ((mediaObj as any).url) {
        return (mediaObj as any).url;
      }
    }
    // Handle DamsMediaItem with EMS ID
    if (mediaObj && typeof mediaObj === 'object' && 'm24' in mediaObj && (mediaObj as any).m24?.ems_id) {
      const damsEmsDomain = Settings.viteEnvironmentIsProduction ? 'ems.dimu.org' : 'ems-test.dimu.org';
      return `https://${damsEmsDomain}/image/${(mediaObj as any).m24.ems_id}`;
    }
    return '';
  }

  let source = 'm24';
  if (mediaObject && 'dimuId' in mediaObject && mediaObject.dimuId) {
    source = 'dm';
  }
  if (mediaObject && 'dmsf_id' in mediaObject && mediaObject.dmsf_id) {
    source = 'dams';
  }

  let sameSite = true;
  let siteName = '';
  if (siteId && mediaObject && 'site_id' in mediaObject && mediaObject.site_id !== siteId) {
    sameSite = false;
    siteName = mediaObject.site_name || '';
  }

  return (
    <Container
      fullWidth
      fullHeight
      column
      gap={2}
      sx={{
        ...(variant === 'default' && {
          backgroundColor: Styles.Colors.LIGHT_GREY,
          p: 2,
          borderRadius: Styles.Dimensions.RADIUS_ROUNDNESS_DEFAULT,
        }),
      }}>
      <Container fullWidth>
        {(!finderType || finderType === FinderType.M24) && (
          <FinderButton
            type={FinderType.M24}
            overrideText={variant === 'banner' ? t('FinderButton.m24_images_video') : t('FinderButton.m24_images')}
            finderProps={{
              defaultResourceType: [MediaResourceType.IMAGE],
              selectableResourceTypes:
                variant === 'banner' ? [MediaResourceType.IMAGE, MediaResourceType.VIDEO] : [MediaResourceType.IMAGE],
              onSelectionConfirmed: (items: Array<GenericMedia<M24MediaModel>>) => {
                resetContentSettings();
                items.forEach((item) => onChangeMediaObject(prepareMediaItemAsM24MediaModel(item.source)));
              },
            }}
            multiSelect={false}
          />
        )}
        {(!finderType || finderType === FinderType.DM) && (
          <FinderButton
            type={FinderType.DM}
            finderProps={{
              onSelectionConfirmed: (items: Array<GenericMedia<DMMediaModel>>) => {
                resetContentSettings();
                items.forEach((item) => onChangeMediaObject(prepareMediaItemAsDMMediaModel(item.source, item.url)));
              },
              pickImages: true,
            }}
            multiSelect={false}
          />
        )}
        {(!finderType || finderType === FinderType.DAMS) && state.selectedSiteData?.owner?.dams_integration_enabled && (
          <FinderButton
            type={FinderType.DAMS}
            finderProps={{
              defaultResourceType: [MediaResourceType.IMAGE],
              selectableResourceTypes: [MediaResourceType.IMAGE],
              onSelectionConfirmed: (items: Array<GenericMedia<DamsMediaItem>>) =>
                items.forEach((item) => onChangeMediaObject(prepareMediaItemAsDAMSMediaModel(item.source))),
            }}
            multiSelect={false}
          />
        )}
      </Container>
      {(!!m24Item?.url ||
        (mediaObject &&
          typeof mediaObject === 'object' &&
          true &&
          'm24' in mediaObject &&
          !!(mediaObject as any).m24?.ems_id) ||
        (mediaObject && isDiMuMediaModel(mediaObject))) && (
        <Container
          p={2}
          column
          fullWidth
          sx={{
            ...(variant === 'banner'
              ? {
                  backgroundColor: Styles.Colors.DARK_GREY,
                  color: Styles.Colors.WHITE,
                }
              : {
                  backgroundColor: Styles.Colors.WHITE,
                  border: `1px solid ${Styles.Colors.MEDIUM_LIGHT_GREY}`,
                }),
            borderRadius: Styles.Dimensions.RADIUS_ROUNDNESS_DEFAULT,
          }}>
          <Container fullWidth spaceBetween top gap={2}>
            <ImageWithFocusPointSelector
              src={getImageSrc(m24Item, mediaObject)}
              mimetype={m24Item?.mimetype || (hasMimetype(mediaObject) ? mediaObject.mimetype : '')}
              title={m24Item?.title || (mediaObject && 'title' in mediaObject ? mediaObject.title : undefined)}
              alt={m24Item?.content?.alttext || (hasLocal(mediaObject) ? mediaObject.local?.caption : undefined)}
              maxWidth='150px'
              x={hasLocal(mediaObject) ? mediaObject.local?.x : '0'}
              y={hasLocal(mediaObject) ? mediaObject.local?.y : '0'}
              onFocusPointChanged={handleFocusPointChanged}
            />

            <Container top column right sx={{ alignSelf: 'stretch', flexGrow: 1 }}>
              <DeleteButton
                onConfirm={() => onChangeMediaObject(null)}
                deleteLabel={tCommon('remove')}
                deleteIcon={<MaterialSymbol color='error' name='do_not_disturb_on' fill />}
              />
              <Container fullWidth left gap={2} mb={1} mt={-5}>
                {source === 'dm' && (
                  <Chip size='small' label='DM' sx={{ fontSize: '0.7em', color: '#fff', backgroundColor: '#D94353' }} />
                )}
                {source === 'm24' && !sameSite && (
                  <Chip
                    size='small'
                    label={`M24${sameSite ? '' : ` / ${siteName}`}`}
                    sx={{ fontSize: '0.7em', backgroundColor: '#C44DBA', color: '#fff' }}
                  />
                )}
                {source === 'dams' && (
                  <Chip
                    size='small'
                    label='DAMS'
                    sx={{ fontSize: '0.7em', backgroundColor: '#785AAC', color: '#fff' }}
                  />
                )}

                <Typography variant='caption'>
                  {isM24Media && (
                    <>
                      {mediaObject?.title || mediaObject?.filename}{' '}
                      <span style={{ color: '#858585ff' }}> / M24-ID: {mediaObject?.id}</span>
                    </>
                  )}
                  {mediaObject && 'dimuId' in mediaObject && <>DM-ID: {(mediaObject as any).dimuId}</>}
                </Typography>
              </Container>
              {children}
              {isM24Media && canEdit && (
                <>
                  <Container left fullWidth>
                    {!m24Item?.content?.alttext && (
                      <Container>
                        <MaterialSymbol name='error' fill style={{ color: Styles.Colors.MEDIUM_RED }} />
                        <Typography variant='caption' color='error'>
                          {t('M24Content.MissingAltTextWarning')}
                        </Typography>
                      </Container>
                    )}
                    <Button
                      sx={{
                        typography: 'caption',
                        textAlign: 'left',
                        marginTop: 'auto',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                      startIcon={<MaterialSymbol name='edit' fill />}
                      onClick={handleEditMedia}>
                      {t('M24Content.EditGlobalMediaData')}
                    </Button>
                  </Container>
                </>
              )}
            </Container>
          </Container>
          {endChildren}
        </Container>
      )}
    </Container>
  );
};
